import React, { useState } from "react";
import {
  BalanceWrapper,
  ButtonWrapper,
  ItemPrice,
  ItemTitle,
  ItemWrapper,
  MainContainer,
  MainHeading,
  MarketplaceLabel,
  MarketplaceRow,
  MarketplaceSelect,
  StyledButton,
  StyledDivider,
  TotalItemTitle,
} from "./styles";
import cache from "utils/cache";
import { message } from "antd";
import { loadStripe } from "@stripe/stripe-js";
import { useCreateStripeCheckoutSessionMutation } from "api/orderApi";
import { calculateOrderTotalWithTax } from "utils/checkoutPricing";

const isCompleteOrderAddress = (address) =>
  Boolean(
    address?.firstName?.trim() &&
    address?.lastName?.trim() &&
    address?.streetAddress?.trim() &&
    address?.city?.trim() &&
    address?.state &&
    address?.country &&
    address?.zip?.trim()
  );

const normalizeOrderAddress = (address) => ({
  firstName: address.firstName?.trim() || "",
  lastName: address.lastName?.trim() || "",
  phoneNumber: address.phoneNumber?.trim() || "",
  streetAddress: address.streetAddress?.trim() || "",
  apartmentNumber: address.apartmentNumber?.trim() || "",
  city: address.city?.trim() || "",
  state: address.state || "",
  country: address.country || "",
  zip: address.zip?.trim() || "",
});

const OrderSummary = ({ shippingSelection }) => {
  const cachedItems = cache.getItem("cartItems") || [];
  const [marketplace, setMarketplace] = useState("SHOPIFY");
  const [createStripeCheckoutSession, { isLoading: isCreatingStripeSession }] =
    useCreateStripeCheckoutSessionMutation();
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

  const {
    useDefaultShippingAddress,
    orderShippingAddress,
    hasSavedDefault,
  } = shippingSelection || {
    useDefaultShippingAddress: true,
    orderShippingAddress: null,
    hasSavedDefault: false,
  };

  const mapOrderItems = () =>
    cachedItems.map((item) => ({
      productId: item.productId,
      variantId: item.variantId,
      quantity: item.productQuantity,
      price:
        item?.variantPrice && item.variantPrice !== 0
          ? Number(item.variantPrice)
          : Number(item.productPrice),
    }));

  const handleNavigate = async () => {
    if (useDefaultShippingAddress && !hasSavedDefault) {
      message.error(
        "No saved shipping address. Please add one in your profile or enter a ship-to for this order."
      );
      return;
    }
    if (!useDefaultShippingAddress && !isCompleteOrderAddress(orderShippingAddress)) {
      message.error(
        "Please complete the ship-to address for this order (name, street, city, state, country, zip)."
      );
      return;
    }
    const response = await createStripeCheckoutSession({
      totalAmount: Number(calculateTotalWithTax()),
      gateway: "STRIPE",
      connectFlow: true,
      items: mapOrderItems(),
      marketplace,
      useDefaultShippingAddress: Boolean(useDefaultShippingAddress),
      ...(useDefaultShippingAddress
        ? {}
        : { shippingAddress: normalizeOrderAddress(orderShippingAddress) }),
    });

    if (response?.error) {
      message.error(response?.error?.data?.message || "Unable to create Stripe checkout");
      return;
    }

    const checkoutUrl = response.data?.link;
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
      return;
    }

    const stripe = await stripePromise;
    if (stripe && response.data?.sessionId) {
      const { error } = await stripe.redirectToCheckout({
        sessionId: response.data.sessionId,
      });
      if (error) {
        message.error(error.message || "Redirect to Stripe failed");
      }
      return;
    }

    message.error("Stripe checkout link not received. Please try again.");
  };

  const getPricing = () => {
    const subtotal = cachedItems.reduce((total, item) => {
      const itemPrice =
        item?.variantPrice && item?.variantPrice !== 0 ? item?.variantPrice : item?.productPrice;
      return total + itemPrice * item?.productQuantity;
    }, 0);
    return calculateOrderTotalWithTax(subtotal);
  };

  const calculateTotalPrice = () => getPricing().subtotal.toFixed(2);
  const calculateTax = () => getPricing().tax.toFixed(2);
  const calculateTotalWithTax = () => getPricing().total.toFixed(2);

  return (
    <MainContainer>
      <MainHeading>{`Order Summary`}</MainHeading>
      <BalanceWrapper>
        <ItemWrapper>
          <ItemTitle>{`Items (${cachedItems?.length})`}</ItemTitle>
          <ItemPrice>{`$${calculateTotalPrice()}`}</ItemPrice>
        </ItemWrapper>
        <ItemWrapper>
          <ItemTitle>{`Tax Collected:`}</ItemTitle>
          <ItemPrice>{`$${calculateTax()}`}</ItemPrice>
        </ItemWrapper>
      </BalanceWrapper>
      <StyledDivider />
      <ItemWrapper>
        <TotalItemTitle>{`Order Total:`}</TotalItemTitle>
        <ItemPrice>{`$${calculateTotalWithTax()}`}</ItemPrice>
      </ItemWrapper>
      <MarketplaceRow>
        <MarketplaceLabel>Order Source Marketplace</MarketplaceLabel>
        <MarketplaceSelect
          value={marketplace}
          onChange={(e) => setMarketplace(e.target.value)}
        >
          <option value="SHOPIFY">Shopify</option>
          <option value="EBAY">eBay</option>
          <option value="AMAZON">Amazon</option>
          <option value="TIKTOK">TikTok</option>
          <option value="WALMART">Walmart</option>
        </MarketplaceSelect>
      </MarketplaceRow>
      <ButtonWrapper>
        <StyledButton onClick={handleNavigate} loading={isCreatingStripeSession}>
          Proceed to payment
        </StyledButton>
      </ButtonWrapper>
    </MainContainer>
  );
};

export default OrderSummary;
